import HBAvatar from "@/components/Avatar";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import Image from "@/components/Image";
import { confirmJoinBoard } from "@/services/board";
import { getBoardType } from "@/utils/board";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { checkLogin } from "@/utils/open";
import useRequest from "@/utils/useReq";
import { Button } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { Case, Default, Switch } from "react-if";
import styles from "./index.module.less";

interface BoardInviteProps {
  board: IBoardInfo;
  inviter: IUser;
  isExpired: boolean;
  inviteCode: string;
  className?: string;
  memberLimit: number;
  memberTotal: number;
  onAccept?: () => void;
  onReject?: () => void;
}

function useBoardInvite(inviteCode: string, onAccept: () => void) {
  const { runAsync: runAccept } = useRequest(
    async () => {
      const user = await checkLogin({
        source_location: "接受邀请",
      });

      if (!user) {
        return;
      }

      const result = await confirmJoinBoard({
        invite_code: inviteCode,
      });

      if (result?.err) {
        // 加入失败
        return;
      }

      HBMessage.success("加入画板成功");
      onAccept?.();
    },
    {
      manual: true,
    }
  );

  return {
    runAccept,
  };
}

export function BoardInviteToast({
  board,
  className,
  inviter,
  isExpired,
  inviteCode,
  memberLimit,
  memberTotal,
  onAccept,
  onReject,
}: BoardInviteProps) {
  const { runAccept: handleAccept } = useBoardInvite(inviteCode, onAccept);

  const handleReject = async () => {
    try {
      // 暂时只关闭 toast，拒绝功能可以后续实现
      onReject?.();
    } catch (error) {
      console.error("拒绝邀请失败:", error);
    }
  };

  const isFull = memberTotal >= memberLimit;

  return (
    <div
      className={classNames(styles.toast, className)}
      data-gd-expose="dialog_expose"
      data-dialog-title="画板邀请弹窗"
      data-board-id={board?.board_id}
      data-board-type={getBoardType(board)}
    >
      <div className={styles.inviter}>
        <HBAvatar user={inviter} size={40} shape="circle" withLink />
      </div>

      <div className={styles.info}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className={styles.inviterName}>{inviter.username}</span>
          邀请你
        </div>
        <div>加入该画板共同采集</div>
      </div>
      <Switch>
        <Case condition={isFull}>
          <div className={styles.actions}>
            <div className={styles.expired}>已满员</div>
          </div>
        </Case>
        <Case condition={isExpired}>
          <div className={styles.actions}>
            <div className={styles.expired}>链接已失效</div>
          </div>
        </Case>
        <Default>
          <div className={styles.actions}>
            <Button
              shape="round"
              onClick={handleReject}
              data-gd-click="button_click"
              data-button-name="拒绝"
            >
              拒绝
            </Button>
            <Button
              shape="round"
              onClick={handleAccept}
              type="primary"
              data-gd-click="button_click"
              data-button-name="加入"
            >
              加入
            </Button>
          </div>
        </Default>
      </Switch>
    </div>
  );
}

export function PrivateBoardInviteView({
  className,
  board,
  inviter,
  isExpired,
  inviteCode,
  memberLimit,
  memberTotal,
  onAccept,
}: BoardInviteProps) {
  const { runAccept: handleAccept } = useBoardInvite(inviteCode, onAccept);
  const isFull = memberTotal >= memberLimit;

  return (
    <div className={classNames(styles.privateBoardInviteView, className)}>
      <div className={styles.inviter}>
        <Link href={`/user/${inviter.urlname}`} className={styles.inviterName}>
          <HBAvatar user={inviter} size={32} shape="circle" />
          <span>{inviter.username}</span>
        </Link>
        邀请你
      </div>
      <div className={styles.info}>加入该画板共同采集</div>
      <div className={styles.boardPreview}>
        <Image
          {...gdImgSrcPolicy(
            "https://grocery-cdn.huaban.com/file/58ce8320985011f0a59ecb9f4a74cfb9.png",
            {
              displayWidth: 244,
            }
          )}
          alt={board.title}
          width={244}
        />
        <div className={styles.privateTag}>
          <HBIcon type="ic_lock" />
        </div>
      </div>
      <Switch>
        <Case condition={isFull}>
          <div className={styles.actions}>
            <div className={styles.expired}>已满员</div>
          </div>
        </Case>
        <Case condition={isExpired}>
          <div className={styles.actions}>
            <div className={styles.expired}>链接已失效</div>
          </div>
        </Case>
        <Default>
          <div className={styles.actions}>
            <Button
              shape="round"
              onClick={handleAccept}
              type="primary"
              data-gd-click="button_click"
              data-button-name="立即加入"
            >
              立即加入
            </Button>
          </div>
        </Default>
      </Switch>
    </div>
  );
}
